import {
  JiraFieldCreateMetadata,
  JiraIssueType,
  JiraLabel,
  JiraPriority,
  JiraProject,
  JiraStatus,
} from '@/models/integrations/integrations.models';
import { organizationData } from '@/store/helpers';
import { useIntegrationsStore } from '@/store/integrationsStore';
import { MaybeArray } from '@/utils/types';
import { cloneDeep, isEmpty, orderBy } from 'lodash-es';
import { computed } from 'vue';
import {
  JiraSupportedCustomFieldBaseType,
  JiraSupportedCustomFieldSubType,
} from './types';
import {
  IntegrationInstallation,
  JiraFieldMappings,
  JiraLabelMappings,
  JiraOptionMappings,
  JiraPriorityMappings,
  JiraProjectMappingValue,
  JiraStatusMappings,
  JiraUserMappings,
  JiraWorkItemTypeMappingValue,
} from '@/api';
import {
  CustomField,
  CustomFieldType,
  CustomFieldTextSubType,
  CustomFieldNumberSubType,
  CustomFieldChoiceSubType,
  CustomFieldDateSubType,
  CustomFieldEntitySubType,
  Priority,
  Label,
  Lane,
  LaneType,
} from '@/api';
import { inferAnything } from '../common/utils';

export const useJiraProjectEntities = (opts: {
  installationId: IntegrationInstallation['id'];
  projectId: JiraProject['id'];
}) => {
  const integrationsStore = useIntegrationsStore();
  const entities = computed(() =>
    integrationsStore.getJiraInstanceEntities(opts.installationId)
  );

  const jiraIssueTypes = computed<JiraIssueType[]>(() => {
    const project = entities.value?.jiraProjects.find(
      (jp) => jp.id === opts.projectId
    );
    if (!project) return [];

    const validIssueTypeIds = new Set<JiraIssueType['id']>(
      project.issueTypes?.map((it) => it.id)
    );

    const ret = entities.value?.jiraIssueTypes.filter(
      (jit) => validIssueTypeIds.has(jit.id) && !jit.subtask
    );

    return orderBy(ret, (it) => it.hierarchyLevel, 'desc');
  });

  /** highest priority first */
  const jiraPriorities = computed<JiraPriority[]>(() => {
    const applicablePriorityScheme = entities.value?.jiraPrioritySchemes.find(
      (scheme) =>
        scheme.projects?.values.find((project) => project.id === opts.projectId)
    );
    const priorities = applicablePriorityScheme?.priorities?.values || [];
    return orderBy(priorities, (p) => parseInt(p.sequence), 'asc');
  });

  const jiraLabels = computed<JiraLabel[]>(() => {
    return entities.value?.jiraLabels || [];
  });

  return {
    jiraIssueTypes,
    jiraPriorities,
    jiraLabels,
  };
};

export const useJiraIssueTypeEntities = (opts: {
  installationId: IntegrationInstallation['id'];
  projectId: JiraProject['id'];
  issueTypeId: JiraIssueType['id'];
}) => {
  const integrationsStore = useIntegrationsStore();
  const entities = computed(() =>
    integrationsStore.getJiraInstanceEntities(opts.installationId)
  );

  const jiraIssueType = computed<JiraIssueType | undefined>(() => {
    return entities.value?.jiraIssueTypes.find(
      (jit) => jit.id === opts.issueTypeId
    );
  });

  type JiraFieldWithDevstrideMeta = {
    field: JiraFieldCreateMetadata;
    jiraMeta: {
      valType: JiraSupportedCustomFieldBaseType;
      cfType: string;
    };
    devstrideMeta: MaybeArray<{
      type: CustomField['type'];
      subType?: CustomField['subType'];
      optionType?: CustomField['optionType'];
      isMultiSelect?: boolean;
    }>;
  };

  const jiraFields = computed<JiraFieldWithDevstrideMeta[]>(() => {
    const allFields =
      entities.value?.jiraProjectIssueTypeFieldMap
        .get(opts.projectId)
        ?.get(opts.issueTypeId) || [];
    const fields: JiraFieldWithDevstrideMeta[] = [];
    allFields.forEach((field) => {
      if (!field.schema?.custom) return;
      const cfType =
        field.schema.custom.split(':')[1] || field.schema.custom || '';
      const valType = field.schema.type as JiraSupportedCustomFieldBaseType;
      if (valType.startsWith('sd-') || cfType.startsWith('sd-')) return; // don't show service desk fields
      if (cfType.startsWith('gh-')) return; // don't show sprint fields

      const devstrideMeta = ((): (typeof fields)[number]['devstrideMeta'] => {
        if (valType === JiraSupportedCustomFieldBaseType.DATE) {
          return {
            type: CustomFieldType.DATE,
            subType: CustomFieldDateSubType.DATE,
          };
        }
        if (valType === JiraSupportedCustomFieldBaseType.DATETIME) {
          return {
            type: CustomFieldType.DATE,
            subType: CustomFieldDateSubType.DATETIME,
          };
        }
        if (valType === JiraSupportedCustomFieldBaseType.NUMBER) {
          // TODO: show both until we have a way to differentiate between them
          return [
            {
              type: CustomFieldType.NUMBER,
              subType: CustomFieldNumberSubType.INTEGER,
            },
            {
              type: CustomFieldType.NUMBER,
              subType: CustomFieldNumberSubType.DECIMAL,
            },
          ];
        }
        if (valType === JiraSupportedCustomFieldBaseType.STRING) {
          if (cfType === JiraSupportedCustomFieldSubType.TEXTAREA) {
            return {
              type: CustomFieldType.TEXT,
              subType: CustomFieldTextSubType.LONG_TEXT,
            };
          }
          if (cfType === JiraSupportedCustomFieldSubType.URL) {
            return {
              type: CustomFieldType.TEXT,
              subType: CustomFieldTextSubType.URL,
            };
          }

          return {
            type: CustomFieldType.TEXT,
            subType: CustomFieldTextSubType.SHORT_TEXT,
          };
        }
        if (valType === JiraSupportedCustomFieldBaseType.OPTION) {
          return {
            type: CustomFieldType.CHOICE,
            subType: CustomFieldChoiceSubType.DROPDOWN,
            // TODO: keep it open for now, but maybe handle this at some point
            // optionType: CustomFieldOptionType,
            isMultiSelect: !!field.schema.configuration?.isMulti,
          };
        }
        if (valType === JiraSupportedCustomFieldBaseType.ARRAY) {
          if (
            cfType === JiraSupportedCustomFieldSubType.MULTI_USER_PICKER ||
            cfType === JiraSupportedCustomFieldSubType.PEOPLE
          ) {
            return {
              type: CustomFieldType.ENTITY,
              subType: CustomFieldEntitySubType.USER,
              isMultiSelect: true,
            };
          }
          if (
            cfType === JiraSupportedCustomFieldSubType.MULTI_CHECKBOXES ||
            cfType === JiraSupportedCustomFieldSubType.MULTI_SELECT
          ) {
            return {
              type: CustomFieldType.CHOICE,
              subType: CustomFieldChoiceSubType.DROPDOWN,
              isMultiSelect: true,
            };
          }

          // nothing matches, allow every multi-select field if there are options
          if (field.allowedValues?.length) {
            return {
              type: CustomFieldType.CHOICE,
              subType: CustomFieldChoiceSubType.DROPDOWN,
              isMultiSelect: true,
            };
          }
        }
        if (valType === JiraSupportedCustomFieldBaseType.USER) {
          if (cfType === JiraSupportedCustomFieldSubType.USER_PICKER) {
            return {
              type: CustomFieldType.ENTITY,
              subType: CustomFieldEntitySubType.USER,
            };
          }
        }
        return {
          type: CustomFieldType.TEXT,
          subType: CustomFieldTextSubType.LONG_TEXT,
        };
      })();

      fields.push({
        field,
        jiraMeta: {
          valType,
          cfType,
        },
        devstrideMeta,
      });
    });
    return fields;
  });

  const jiraStatuses = computed<JiraStatus[]>(() => {
    const issueTypesWithStatus =
      entities.value?.jiraProjectIssueTypeStatusMap.get(opts.projectId);

    const ret: JiraStatus[] = [];
    for (const issueTypeWithStatus of issueTypesWithStatus || []) {
      if (issueTypeWithStatus.id !== opts.issueTypeId) continue;

      for (const status of issueTypeWithStatus.statuses) {
        ret.push(status);
      }
    }

    return ret;
  });

  return {
    jiraIssueType,
    jiraFields,
    jiraStatuses,
  };
};

/** will return incorrect results if entities (including fields) are not loaded */
export const useJiraIssueTypeMappingStats = (opts: {
  installationId: IntegrationInstallation['id'];
  projectId: JiraProject['id'];
  issueTypeId: JiraIssueType['id'];
  mappings: JiraWorkItemTypeMappingValue | undefined;
}) => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const { jiraPriorities, jiraLabels } = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.projectId,
  });

  const { jiraFields, jiraStatuses } = useJiraIssueTypeEntities({
    installationId: opts.installationId,
    projectId: opts.projectId,
    issueTypeId: opts.issueTypeId,
  });

  type SectionMappingProgress = {
    total: number;
    mapped: number;
    percentComplete: number;
  };
  type IssueTypeMappingProgress = {
    overall: SectionMappingProgress;
    laneMappings: SectionMappingProgress;
    fieldMappings: SectionMappingProgress;
    tagMappings: SectionMappingProgress;
    priorityMappings: SectionMappingProgress;
  };

  const getPercentComplete = (
    total: number,
    mapped: number
  ): SectionMappingProgress['percentComplete'] => {
    if (total === 0) return 100; // nothing to map so it's complete
    let ret = Math.round((mapped / total) * 100);
    if (ret > 100) ret = 100; // we're doing a rough calculation based on the number of keys in the mappings, so this could happen
    return ret;
  };
  const progress = computed<IssueTypeMappingProgress>(() => {
    const laneMappings = {
      total: jiraStatuses.value.length,
      mapped: Object.keys(opts.mappings?.laneMappings ?? 0).length,
      percentComplete: 0,
    };
    laneMappings.percentComplete = getPercentComplete(
      laneMappings.total,
      laneMappings.mapped
    );

    const fieldMappings = {
      total: jiraFields.value.length,
      mapped: Object.keys(opts.mappings?.fieldMappings ?? 0).length,
      percentComplete: 0,
    };
    fieldMappings.percentComplete = getPercentComplete(
      fieldMappings.total,
      fieldMappings.mapped
    );

    const tagMappings = {
      total: jiraLabels.value.length,
      mapped: Object.keys(opts.mappings?.tagMappings ?? 0).length,
      percentComplete: 0,
    };
    tagMappings.percentComplete = getPercentComplete(
      tagMappings.total,
      tagMappings.mapped
    );

    const priorityMappings = {
      total: jiraPriorities.value.length,
      mapped: Object.keys(opts.mappings?.priorityMappings ?? 0).length,
      percentComplete: 0,
    };
    priorityMappings.percentComplete = getPercentComplete(
      priorityMappings.total,
      priorityMappings.mapped
    );

    const overall = {
      total:
        laneMappings.total +
        fieldMappings.total +
        tagMappings.total +
        priorityMappings.total,
      mapped:
        laneMappings.mapped +
        fieldMappings.mapped +
        tagMappings.mapped +
        priorityMappings.mapped,
      percentComplete: 0,
    };
    overall.percentComplete = getPercentComplete(overall.total, overall.mapped);

    return {
      laneMappings,
      fieldMappings,
      tagMappings,
      priorityMappings,
      overall,
    };
  });

  return {
    progress,
  };
};

const trim = (str: string) => str.toLowerCase().split(' ').join('');

/**
 * Strategy:
 * - if it's not set, we'll try to infer from the reference
 * - try to match by name (exact)
 * - try to match by name (fuzzy)
 * - use the default lane collection
 */
export const inferProjectLaneCollection = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraProjectMappingValue['laneCollectionId'] => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const laneCollectionId = opts.subject.projectMappings.laneCollectionId;
  if (laneCollectionId) return laneCollectionId;

  const referenceLaneCollectionId =
    opts.reference?.projectMappings.laneCollectionId;
  if (referenceLaneCollectionId) return referenceLaneCollectionId;

  const defaultLaneCollection = organization.value.laneCollections.find(
    (lc) => lc.default
  );
  if (!defaultLaneCollection)
    throw new Error('Bug in code - default laneCollection not found');

  return defaultLaneCollection.id;
};

/**
 * Strategy:
 * - if it's not set, we'll try to infer by project name against the root folders (exact)
 * - try to infer by project name against the root folders (fuzzy)
 * - let the user handle it
 */
export const inferProjectWorkstream = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraProjectMappingValue['parentNumber'] => {
  const { organization, allRootFolders } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workstreamNumber = opts.subject.projectMappings.parentNumber;
  if (workstreamNumber) return workstreamNumber;

  // check if we can infer from reference
  const referenceWorkstreamNumber =
    opts.reference?.projectMappings.parentNumber;
  if (referenceWorkstreamNumber) return referenceWorkstreamNumber;

  const exactWorkstream = allRootFolders.value.find(
    (folder) => trim(folder.title) === trim(opts.subject.project.name)
  );
  if (exactWorkstream) return exactWorkstream.number;

  const fuzzyWorkstream = allRootFolders.value.find(
    (folder) =>
      trim(folder.title).includes(trim(opts.subject.project.name)) ||
      trim(opts.subject.project.name).includes(trim(folder.title))
  );
  if (fuzzyWorkstream) return fuzzyWorkstream.number;

  return undefined;
};

/**
 * Strategy:
 * - if it's not set, we'll try to infer from the reference
 * - try to match by name (exact)
 * - try to match by name (fuzzy)
 * - use the default workType
 */
export const inferIssueTypeWorkType = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraWorkItemTypeMappingValue['workTypeId'] => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (workType) return workType.id; // we already have a mapping

  if (opts.reference) {
    const referenceWorkTypeId =
      opts.reference?.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ]?.workTypeId;
    if (referenceWorkTypeId) return referenceWorkTypeId;

    const refEntities = useJiraProjectEntities({
      installationId: opts.installationId,
      projectId: opts.reference.project.id,
    });
    // try to match by name (exact)
    const exactIssueTypeInRef = refEntities.jiraIssueTypes.value.find(
      (jit) => trim(jit.name) === trim(opts.subject.issueType.name)
    );
    if (exactIssueTypeInRef) {
      const exactWorkType = organization.value.workTypes.find(
        (wt) =>
          wt.id ===
          opts.reference?.projectMappings.workTypeMappings[
            exactIssueTypeInRef.id
          ]?.workTypeId
      );
      if (exactWorkType) return exactWorkType.id;
    }
    // that's it, proceed to heuristics
  }

  const exactWorkType = organization.value.workTypes.find(
    (wt) => trim(wt.title) === trim(opts.subject.issueType.name)
  );
  if (exactWorkType) return exactWorkType.id;

  const fuzzyWorkType = organization.value.workTypes.find(
    (wt) =>
      trim(wt.title).includes(trim(opts.subject.issueType.name)) ||
      trim(opts.subject.issueType.name).includes(trim(wt.title))
  );
  if (fuzzyWorkType) return fuzzyWorkType.id;

  const defaultWorkType = organization.value.workTypes.find((wt) => wt.default);
  if (!defaultWorkType)
    throw new Error('Bug in code - default workType not found');

  return defaultWorkType.id;
};

/**
 * Strategy:
 * - if lane mapping is not supported, just return the current mappings (empty or otherwise)
 * - try to infer the lane mappings from the reference
 * - try to match by name (exact)
 * - try to match by name (fuzzy)
 * - try to match by heuristics by type (new)
 * - try to match by heuristics by type (done)
 * - map to the first custom lane and be done with it (if it exists)
 * - let the user handle it
 */
export const inferIssueTypeLaneMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraStatusMappings => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const { jiraStatuses } = useJiraIssueTypeEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
    issueTypeId: opts.subject.issueType.id,
  });

  const refEntities = opts.reference
    ? useJiraProjectEntities({
        installationId: opts.installationId,
        projectId: opts.reference.project.id,
      })
    : undefined;

  let referenceIssueTypeId: JiraIssueType['id'] | undefined = undefined;
  const referenceLaneMappings: JiraStatusMappings | undefined = (() => {
    const lm =
      opts.reference?.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ]?.laneMappings;
    if (lm) return lm;

    // try to find the reference workType by name
    const refIssueType = refEntities?.jiraIssueTypes.value.find(
      (jit) => trim(jit.name) === trim(opts.subject.issueType.name)
    );
    if (!refIssueType) return undefined;
    referenceIssueTypeId = refIssueType.id;

    return opts.reference?.projectMappings.workTypeMappings[refIssueType.id]
      ?.laneMappings;
  })();

  const refIssueTypeEntities =
    opts.reference && referenceIssueTypeId
      ? useJiraIssueTypeEntities({
          installationId: opts.installationId,
          projectId: opts.reference.project.id,
          issueTypeId: referenceIssueTypeId,
        })
      : undefined;

  const nextLaneMappings: JiraStatusMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .laneMappings || {}
  );

  const availableLanes =
    organization.value.laneCollections
      .find((lc) => lc.id === opts.subject.projectMappings.laneCollectionId)
      ?.lanes.map((laneId) =>
        organization.value?.lanes.find((lane) => lane.id === laneId.laneId)
      )
      .filter((l): l is Lane => !!l) || [];

  for (const jiraStatus of jiraStatuses.value) {
    // check if it already has a mapping, do not override
    if (nextLaneMappings[jiraStatus.id]) continue;

    // check if we can infer from reference
    const referenceLaneId = (() => {
      const rlId = referenceLaneMappings?.[jiraStatus.id];
      if (rlId) return rlId;

      // try to find the reference lane by name
      const refStatus = refIssueTypeEntities?.jiraStatuses.value.find(
        (s) => trim(s.name) === trim(jiraStatus.name)
      );
      if (!refStatus) return undefined;

      return referenceLaneMappings?.[refStatus.id];
    })();

    if (
      referenceLaneId &&
      availableLanes.some((l) => l.id === referenceLaneId)
    ) {
      nextLaneMappings[jiraStatus.id] = referenceLaneId;
      continue; // we're done with this lane
    }

    // try to match by name (exact)
    const exactLane = availableLanes.find(
      (l) => trim(l.name) === trim(jiraStatus.name)
    );
    if (exactLane) {
      nextLaneMappings[jiraStatus.id] = exactLane.id;
      continue; // we're done with this lane
    }

    // try to match by name (fuzzy)
    const fuzzyLane = availableLanes.find(
      (l) =>
        trim(l.name).includes(trim(jiraStatus.name)) ||
        trim(jiraStatus.name).includes(trim(l.name))
    );
    if (fuzzyLane) {
      nextLaneMappings[jiraStatus.id] = fuzzyLane.id;
      continue; // we're done with this lane
    }

    // try to match by heuristics by type (new)
    if (['todo', 'backlog', 'new'].includes(trim(jiraStatus.name))) {
      const lane = availableLanes.find((l) => l.type === LaneType.NEW);
      if (lane) nextLaneMappings[jiraStatus.id] = lane.id;
      continue;
    }

    // try to match by heuristics by type (done)
    if (['done', 'close', 'complete'].includes(trim(jiraStatus.name))) {
      const lane = availableLanes.find((l) => l.type === LaneType.DONE);
      if (lane) nextLaneMappings[jiraStatus.id] = lane.id;
      continue;
    }

    // just map to the first custom lane and be done with it
    const firstCustomLane = availableLanes.find(
      (l) => l.type === LaneType.CUSTOM
    );
    if (firstCustomLane) nextLaneMappings[jiraStatus.id] = firstCustomLane.id;
  }

  return nextLaneMappings;
};

/**
 * Strategy:
 * - try to infer the tag mappings from the reference
 * - try to match by name (exact)
 * - try to match by name (fuzzy)
 * - let the user handle it
 */
export const inferIssueTypeTagMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraLabelMappings => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraLabels = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  }).jiraLabels;

  const refEntities = opts.reference
    ? useJiraProjectEntities({
        installationId: opts.installationId,
        projectId: opts.reference.project.id,
      })
    : undefined;

  const referenceTagMappings: JiraLabelMappings | undefined = (() => {
    const tm =
      opts.reference?.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ]?.tagMappings;
    if (tm) return tm;

    // try to find the reference workType by name
    const refIssueType = refEntities?.jiraIssueTypes.value.find(
      (jit) => trim(jit.name) === trim(opts.subject.issueType.name)
    );
    if (!refIssueType) return undefined;

    return opts.reference?.projectMappings.workTypeMappings[refIssueType.id]
      ?.tagMappings;
  })();

  const nextTagMappings: JiraLabelMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .tagMappings || {}
  );

  const availableLabels =
    organization.value.labelCollections
      .find((lc) => lc.id === workType.labelCollectionId)
      ?.labelIds.map((labelId) =>
        organization.value?.labels.find((label) => label.id === labelId)
      )
      .filter((l): l is Label => !!l) || [];

  for (const tag of jiraLabels.value) {
    // check if it already has a mapping, do not override
    if (nextTagMappings[tag]) continue;

    // check if we can infer from reference
    const referenceTagId = (() => {
      const rtId = referenceTagMappings?.[tag];
      if (rtId) return rtId;

      // try to find the reference tag by name
      const refTag = refEntities?.jiraLabels.value.find(
        (l) => trim(l) === trim(tag)
      );
      if (!refTag) return undefined;

      return referenceTagMappings?.[refTag];
    })();

    if (
      referenceTagId &&
      availableLabels.some((l) => l.id === referenceTagId)
    ) {
      nextTagMappings[tag] = referenceTagId;
      continue; // we're done with this tag
    }

    // try to match by name (exact)
    const exactTag = availableLabels.find((l) => trim(l.name) === trim(tag));
    if (exactTag) {
      nextTagMappings[tag] = exactTag.id;
      continue; // we're done with this tag
    }

    // try to match by name (fuzzy)
    const fuzzyTag = availableLabels.find(
      (l) =>
        trim(l.name).includes(trim(tag)) || trim(tag).includes(trim(l.name))
    );
    if (fuzzyTag) {
      nextTagMappings[tag] = fuzzyTag.id;
      continue; // we're done with this tag
    }

    // do nothing, let the user handle it
  }

  return nextTagMappings;
};

/**
 * Strategy:
 * - try to infer the priority mappings from the reference
 * - try to match by name (exact)
 * - try to match by name (fuzzy)
 * - match to the current priority index
 * - match to the default priority
 * - let the user handle it
 */
export const inferIssueTypePriorityMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraPriorityMappings => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraPriorities = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  }).jiraPriorities;

  const refEntities = opts.reference
    ? useJiraProjectEntities({
        installationId: opts.installationId,
        projectId: opts.reference.project.id,
      })
    : undefined;

  const referencePriorityMappings: JiraPriorityMappings | undefined = (() => {
    const pm =
      opts.reference?.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ]?.priorityMappings;
    if (pm) return pm;

    // try to find the reference workType by name
    const refIssueType = refEntities?.jiraIssueTypes.value.find(
      (jit) => trim(jit.name) === trim(opts.subject.issueType.name)
    );
    if (!refIssueType) return undefined;

    return opts.reference?.projectMappings.workTypeMappings[refIssueType.id]
      ?.priorityMappings;
  })();

  const nextPriorityMappings: JiraPriorityMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .priorityMappings || {}
  );

  const priorityCollection = organization.value.priorityCollections.find(
    (pc) => pc.id === workType.priorityCollectionId
  );
  const availablePriorities = orderBy(
    priorityCollection?.priorityIds
      .map((pId) =>
        organization.value?.priorities.find((priority) => priority.id === pId)
      )
      .filter((p): p is Priority => !!p) || [],
    'asc'
  );

  let currentPriorityIndexToUse = 0;

  for (const priority of jiraPriorities.value) {
    // check if it already has a mapping, do not override
    if (nextPriorityMappings[priority.id]) continue;

    // check if we can infer from reference
    const referencePriorityId = (() => {
      const rpId = referencePriorityMappings?.[priority.id];
      if (rpId) return rpId;

      // try to find the reference priority by name
      const refPriority = refEntities?.jiraPriorities.value.find(
        (p) => trim(p.name) === trim(priority.name)
      );
      if (!refPriority) return undefined;

      return referencePriorityMappings?.[refPriority.id];
    })();

    if (
      referencePriorityId &&
      availablePriorities.some((p) => p.id === referencePriorityId)
    ) {
      nextPriorityMappings[priority.id] = referencePriorityId;
      continue; // we're done with this priority
    }

    // try to match by name (exact)
    const exactPriority = availablePriorities.find(
      (p) => trim(p.name) === trim(priority.name)
    );
    if (exactPriority) {
      nextPriorityMappings[priority.id] = exactPriority.id;
      continue; // we're done with this priority
    }

    // try to match by name (fuzzy)
    const fuzzyPriority = availablePriorities.find(
      (p) =>
        trim(p.name).includes(trim(priority.name)) ||
        trim(priority.name).includes(trim(p.name))
    );
    if (fuzzyPriority) {
      nextPriorityMappings[priority.id] = fuzzyPriority.id;
      continue; // we're done with this priority
    }

    // match to the current priority index
    const currentPriority = availablePriorities[currentPriorityIndexToUse++];
    if (currentPriority) {
      nextPriorityMappings[priority.id] = currentPriority.id;
      continue; // we're done with this priority
    }

    // match to the default priority
    const defaultPriority = availablePriorities.find(
      (p) => p.id === priorityCollection?.defaultPriorityId
    );
    if (defaultPriority) {
      nextPriorityMappings[priority.id] = defaultPriority.id;
      continue; // we're done with this priority
    }

    // do nothing, let the user handle it (shouldn't happen because of the default priority)
  }

  return nextPriorityMappings;
};

/**
 * Strategy:
 *  - try to infer the field mappings from the reference
 *  - try to match by type compatibility and name (exact)
 *  - try to match by type compatibility and name (fuzzy)
 *  - let the user handle it
 *
 *  - for choice fields, also infer the options using the same reference->exact->fuzzy strategy,
 *    if that fails, map to the current option index and hope for the best
 */
export const inferIssueTypeFieldMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraFieldMappings => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraFields = useJiraIssueTypeEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
    issueTypeId: opts.subject.issueType.id,
  }).jiraFields.value;

  const refEntities = opts.reference
    ? useJiraProjectEntities({
        installationId: opts.installationId,
        projectId: opts.reference.project.id,
      })
    : undefined;

  let referenceIssueTypeId: JiraIssueType['id'] | undefined = undefined;

  const referenceFieldMappings: JiraFieldMappings | undefined = (() => {
    const fm =
      opts.reference?.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ]?.fieldMappings;
    if (fm) return fm;

    // try to find the reference workType by name
    const refIssueType = refEntities?.jiraIssueTypes.value.find(
      (jit) => trim(jit.name) === trim(opts.subject.issueType.name)
    );
    if (!refIssueType) return undefined;
    referenceIssueTypeId = refIssueType.id;

    return opts.reference?.projectMappings.workTypeMappings[refIssueType.id]
      ?.fieldMappings;
  })();

  const refIssueTypeEntities =
    opts.reference && referenceIssueTypeId
      ? useJiraIssueTypeEntities({
          installationId: opts.installationId,
          projectId: opts.reference.project.id,
          issueTypeId: referenceIssueTypeId,
        })
      : undefined;

  const nextFieldMappings: JiraFieldMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .fieldMappings || {}
  );

  const customFieldCollection = organization.value.customFieldCollections.find(
    (cfc) => cfc.id === workType.customFieldCollectionId
  );
  if (!customFieldCollection)
    throw new Error('Bug in code - customFieldCollection not found');
  const dsCustomFields = organization.value.customFields
    // filter on workType
    .filter((cf) =>
      customFieldCollection.customFields.some(
        (collectionCf) => collectionCf.customFieldId === cf.id
      )
    );

  for (const jiraField of jiraFields) {
    const fieldMapping = nextFieldMappings[jiraField.field.fieldId];
    if (fieldMapping) continue; // we already have a mapping, do not override

    // try to infer from the reference
    const referenceFieldMapping = (() => {
      const rfMapping = referenceFieldMappings?.[jiraField.field.fieldId];
      if (rfMapping) return rfMapping;

      // try to find the reference field by name
      const refField = refIssueTypeEntities?.jiraFields.value.find(
        (f) => trim(f.field.name) === trim(jiraField.field.name)
      );
      if (!refField) return undefined;

      return referenceFieldMappings?.[refField.field.fieldId];
    })();
    if (
      referenceFieldMapping &&
      dsCustomFields.some((cf) => cf.id === referenceFieldMapping.customFieldId)
    ) {
      nextFieldMappings[jiraField.field.fieldId] = referenceFieldMapping;

      // TODO: delete this if option mappings are acting up when copying from another project
      if (referenceFieldMapping.optionMappings) {
        nextFieldMappings[jiraField.field.fieldId].optionMappings =
          referenceFieldMapping.optionMappings;
      }
      continue; // we're done with this field
    }

    // prepare a list of compatible custom fields
    const compatibleCustomFields = dsCustomFields.filter((cf) => {
      const fieldFilter = jiraField.devstrideMeta;
      if (Array.isArray(fieldFilter)) {
        if (!fieldFilter.length) return true;

        const someFiltersMatch = fieldFilter.some((filter) => {
          if (filter.type && cf.type !== filter.type) {
            return false;
          }
          if (filter.subType && cf.subType !== filter.subType) {
            return false;
          }
          if (filter.optionType && cf.optionType !== filter.optionType) {
            return false;
          }
          if (filter.isMultiSelect && !cf.isMultiSelect) {
            return false;
          }
          return true;
        });
        if (!someFiltersMatch) return false;
      } else if (fieldFilter) {
        if (fieldFilter.type && cf.type !== fieldFilter.type) {
          return false;
        }
        if (fieldFilter.subType && cf.subType !== fieldFilter.subType) {
          return false;
        }
        if (
          fieldFilter.optionType &&
          cf.optionType !== fieldFilter.optionType
        ) {
          return false;
        }
        if (fieldFilter.isMultiSelect && !cf.isMultiSelect) {
          return false;
        }
      }

      return true;
    });

    // prepare option mapper to be used later
    const prepareOptionMappings = (field: CustomField): JiraOptionMappings => {
      const referenceOptionMappings =
        referenceFieldMappings?.[jiraField.field.fieldId]?.optionMappings;
      const availableOptions = field.options || [];
      let currentOptionIndexToUse = 0;

      const optionMappings: JiraOptionMappings = {};
      for (const jiraOption of jiraField.field.allowedValues || []) {
        const optionMapping = referenceOptionMappings?.[jiraOption.id];
        const optionValue = jiraOption.value || jiraOption.name || '';

        if (
          optionMapping &&
          availableOptions.some((o) => o.id === optionMapping)
        ) {
          optionMappings[jiraOption.id] = optionMapping;
          continue; // we're done with this option
        }

        // try to match by name (exact)
        const exactOption = availableOptions.find(
          (o) => trim(o.label) === trim(optionValue)
        );
        if (exactOption) {
          optionMappings[jiraOption.id] = exactOption.id;
          continue; // we're done with this option
        }

        // try to match by name (fuzzy)
        const fuzzyOption = availableOptions.find(
          (o) =>
            trim(o.label).includes(trim(optionValue)) ||
            trim(optionValue).includes(trim(o.label))
        );
        if (fuzzyOption) {
          optionMappings[jiraOption.id] = fuzzyOption.id;
          continue; // we're done with this option
        }

        // match to the current option index and hope for the best
        const currentOption = availableOptions[currentOptionIndexToUse++];
        if (currentOption) {
          optionMappings[jiraOption.id] = currentOption.id;
          continue; // we're done with this option
        }
      }

      return optionMappings;
    };

    // try to match by name (exact)
    const exactField = compatibleCustomFields.find(
      (cf) => trim(cf.name) === trim(jiraField.field.name)
    );
    if (exactField) {
      nextFieldMappings[jiraField.field.fieldId] = {
        customFieldId: exactField.id,
        optionMappings: prepareOptionMappings(exactField),
      };
      continue; // we're done with this field
    }

    // try to match by name (fuzzy)
    const fuzzyField = compatibleCustomFields.find(
      (cf) =>
        trim(cf.name).includes(trim(jiraField.field.name)) ||
        trim(jiraField.field.name).includes(trim(cf.name))
    );
    if (fuzzyField) {
      nextFieldMappings[jiraField.field.fieldId] = {
        customFieldId: fuzzyField.id,
        optionMappings: prepareOptionMappings(fuzzyField),
      };
      continue; // we're done with this field
    }

    // let the user handle it
  }

  return nextFieldMappings;
};

/**
 * combines all the infer functions to infer the issue type mappings
 */
export const inferIssueTypeEntityMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): JiraWorkItemTypeMappingValue => {
  const DEFAULT_VALUE: Readonly<JiraWorkItemTypeMappingValue> = {
    workTypeId: '',
    laneMappings: {},
    fieldMappings: {},
    tagMappings: {},
    priorityMappings: {},
  };
  const next: JiraWorkItemTypeMappingValue =
    cloneDeep<JiraWorkItemTypeMappingValue>(
      opts.subject.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ] || DEFAULT_VALUE
    );

  const args = (): typeof opts => {
    const updatedProjectMappings: JiraProjectMappingValue = {
      ...opts.subject.projectMappings,
      workTypeMappings: {
        ...opts.subject.projectMappings.workTypeMappings,
        [opts.subject.issueType.id]: next,
      },
    };
    const ret: typeof opts = {
      installationId: opts.installationId,
      subject: {
        project: opts.subject.project,
        issueType: opts.subject.issueType,
        projectMappings: updatedProjectMappings,
      },
      reference: opts.reference,
    };
    return ret;
  };
  next.workTypeId = inferIssueTypeWorkType(args());
  next.laneMappings = inferIssueTypeLaneMappings(args());
  next.tagMappings = inferIssueTypeTagMappings(args());
  next.priorityMappings = inferIssueTypePriorityMappings(args());
  next.fieldMappings = inferIssueTypeFieldMappings(args());

  return next;
};

/**
 * combines all the infer functions to infer the project mappings
 */
export const inferProjectMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
  reference?: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}) => {
  const integrationsStore = useIntegrationsStore();

  const entities = integrationsStore.getJiraInstanceEntities(
    opts.installationId
  );
  if (!entities)
    throw new Error('Bug in code - entities not found, did you load them?');

  const next = cloneDeep(opts.subject.projectMappings);

  if (!next.isEnabled) throw new Error('Bug in code - project is disabled');

  next.laneCollectionId = inferProjectLaneCollection({
    installationId: opts.installationId,
    subject: opts.subject,
    reference: opts.reference,
  });

  next.parentNumber = inferProjectWorkstream({
    installationId: opts.installationId,
    subject: opts.subject,
    reference: opts.reference,
  });
  if (!next.parentNumber) return next; // don't infer anything else if no workstream is set

  const { jiraIssueTypes } = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  });

  for (const issueType of jiraIssueTypes.value) {
    next.workTypeMappings[issueType.id] = inferIssueTypeEntityMappings({
      installationId: opts.installationId,
      subject: {
        project: opts.subject.project,
        issueType,
        projectMappings: next,
      },
      reference: opts.reference,
    });
  }

  return next;
};

/**
 * Strategy:
 *  for each user:
 *  - skip if mapped
 *  - match to existing user by email (if provided by Jira)
 *  - match to existing user by name (exact)
 *  - match to existing user by name (fuzzy with 75% match)
 *  - use the email value (if provided by Jira)
 *  - let the user handle it
 */
export const inferInstallationUserMappings = (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    userMappings: JiraUserMappings;
  };
}): JiraUserMappings => {
  const { orgMembershipCache } = organizationData();

  const entities = useIntegrationsStore().getJiraInstanceEntities(
    opts.installationId
  );

  const next = cloneDeep(opts.subject.userMappings);

  for (const jiraUser of entities?.jiraUsers || []) {
    const userMapping = next[jiraUser.accountId];
    if (userMapping) continue; // we already have a mapping, do not override

    // match to existing user by email (if provided by Jira)
    const exactMatchByEmail = jiraUser.emailAddress
      ? orgMembershipCache.value.find(
          (u) => u.user.email === jiraUser.emailAddress
        )
      : undefined;
    if (exactMatchByEmail) {
      next[jiraUser.accountId] = exactMatchByEmail.userId;
      continue;
    }

    // match to existing user by name (exact)
    const exactMatch = orgMembershipCache.value.find(
      (u) =>
        u.user.name &&
        jiraUser.displayName &&
        trim(u.user.name) === trim(jiraUser.displayName)
    );
    if (exactMatch) {
      next[jiraUser.accountId] = exactMatch.userId;
      continue;
    }

    // match to existing user by name (fuzzy with 75% match)
    const fuzzyMatch = orgMembershipCache.value.find((u) => {
      const a = trim(u.user.name || '');
      const b = trim(jiraUser.displayName || '');
      const hasMatch = a.includes(b) || b.includes(a);
      const lenRatio =
        Math.abs(a.length - b.length) / Math.max(a.length, b.length);
      return hasMatch && lenRatio < 0.25; // 75% match (within 25% length difference)
    });
    if (fuzzyMatch) {
      next[jiraUser.accountId] = fuzzyMatch.userId;
      continue;
    }

    // use the raw email value (if provided by Jira) so backend can create a user
    if (jiraUser.emailAddress) {
      next[jiraUser.accountId] = jiraUser.emailAddress;
      continue;
    }

    // do nothing, let the user handle it
  }

  return next;
};

export const inferIssueTypeWorkTypeAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraWorkItemTypeMappingValue['workTypeId']> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (workType) return workType.id;

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Issue Types',
    entity1: [
      { label: opts.subject.issueType.name, value: opts.subject.issueType.id },
    ],

    entity2Name: 'DevStride Work Types',
    entity2: organization.value.workTypes.map((wt) => ({
      label: wt.title,
      value: wt.id,
    })),
  });

  const aiMapping = aiMappings.find(
    (mapping) => mapping.e1.value === opts.subject.issueType.id
  );
  if (aiMapping) return aiMapping.e2.value;

  const defaultWorkType = organization.value.workTypes.find((wt) => wt.default);
  if (!defaultWorkType)
    throw new Error('Bug in code - default workType not found');

  return defaultWorkType.id;
};

export const inferIssueTypeLaneMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraStatusMappings> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const { jiraStatuses } = useJiraIssueTypeEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
    issueTypeId: opts.subject.issueType.id,
  });

  const nextLaneMappings: JiraStatusMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .laneMappings || {}
  );

  const availableLanes =
    organization.value.laneCollections
      .find((lc) => lc.id === opts.subject.projectMappings.laneCollectionId)
      ?.lanes.map((laneId) =>
        organization.value?.lanes.find((lane) => lane.id === laneId.laneId)
      )
      .filter((l): l is Lane => !!l) || [];

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Statuses',
    entity1: jiraStatuses.value.map((status) => ({
      label: status.name,
      value: status.id,
    })),

    entity2Name: 'DevStride Lanes',
    entity2: availableLanes.map((lane) => ({
      label: lane.name,
      value: lane.id,
    })),
  });

  for (const { e1, e2 } of aiMappings) {
    if (!nextLaneMappings[e1.value]) {
      nextLaneMappings[e1.value] = e2.value;
    }
  }

  return nextLaneMappings;
};

export const inferIssueTypeTagMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraLabelMappings> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraLabels = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  }).jiraLabels;

  const nextTagMappings: JiraLabelMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .tagMappings || {}
  );

  const availableLabels =
    organization.value.labelCollections
      .find((lc) => lc.id === workType.labelCollectionId)
      ?.labelIds.map((labelId) =>
        organization.value?.labels.find((label) => label.id === labelId)
      )
      .filter((l): l is Label => !!l) || [];

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Labels',
    entity1: jiraLabels.value.map((label) => ({
      label,
      value: label,
    })),

    entity2Name: 'DevStride Labels or Tags',
    entity2: availableLabels.map((label) => ({
      label: label.name,
      value: label.id,
    })),
  });

  for (const { e1, e2 } of aiMappings) {
    if (!nextTagMappings[e1.value]) {
      nextTagMappings[e1.value] = e2.value;
    }
  }

  return nextTagMappings;
};

export const inferIssueTypePriorityMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraPriorityMappings> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraPriorities = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  }).jiraPriorities;

  const nextPriorityMappings: JiraPriorityMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .priorityMappings || {}
  );

  const priorityCollection = organization.value.priorityCollections.find(
    (pc) => pc.id === workType.priorityCollectionId
  );
  const availablePriorities = orderBy(
    priorityCollection?.priorityIds
      .map((pId) =>
        organization.value?.priorities.find((priority) => priority.id === pId)
      )
      .filter((p): p is Priority => !!p) || [],
    (p) => priorityCollection?.priorityIds.indexOf(p.id) || 0,
    'asc'
  );

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Priorities',
    entity1: jiraPriorities.value.map((priority) => ({
      label: priority.name,
      value: priority.id,
    })),

    entity2Name: 'DevStride Priorities',
    entity2: availablePriorities.map((priority) => ({
      label: priority.name,
      value: priority.id,
    })),
  });

  for (const { e1, e2 } of aiMappings) {
    if (!nextPriorityMappings[e1.value]) {
      nextPriorityMappings[e1.value] = e2.value;
    }
  }

  return nextPriorityMappings;
};

export const inferIssueTypeFieldMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraFieldMappings> => {
  const { organization } = organizationData();
  if (!organization.value)
    throw new Error('Bug in code - organization not found');

  const workType = organization.value.workTypes.find(
    (wt) =>
      wt.id ===
      opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
        ?.workTypeId
  );
  if (!workType) throw new Error('Bug in code - workType not found');

  const jiraFields = useJiraIssueTypeEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
    issueTypeId: opts.subject.issueType.id,
  }).jiraFields.value;

  const nextFieldMappings: JiraFieldMappings = cloneDeep(
    opts.subject.projectMappings.workTypeMappings[opts.subject.issueType.id]
      .fieldMappings || {}
  );

  const customFieldCollection = organization.value.customFieldCollections.find(
    (cfc) => cfc.id === workType.customFieldCollectionId
  );
  if (!customFieldCollection)
    throw new Error('Bug in code - customFieldCollection not found');
  const dsCustomFields = organization.value.customFields.filter((cf) =>
    customFieldCollection.customFields.some(
      (collectionCf) => collectionCf.customFieldId === cf.id
    )
  );

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Fields',
    entity1: jiraFields.map((field) => ({
      label: field.field.name,
      value: field.field.fieldId,
    })),

    entity2Name: 'DevStride Custom Fields',
    entity2: dsCustomFields.map((field) => ({
      label: field.name,
      value: field.id,
    })),
  });

  for (const { e1, e2 } of aiMappings) {
    // continue if the field is already mapped
    if (nextFieldMappings[e1.value]) continue;

    // find compatible custom fields and only apply the mapping if it's compatible
    const compatibleCustomFields = dsCustomFields.filter((cf) => {
      const fieldFilter = jiraFields.find(
        (jf) => jf.field.fieldId === e1.value
      )?.devstrideMeta;
      if (Array.isArray(fieldFilter)) {
        if (!fieldFilter.length) return true;

        const someFiltersMatch = fieldFilter.some((filter) => {
          if (filter.type && cf.type !== filter.type) {
            return false;
          }
          if (filter.subType && cf.subType !== filter.subType) {
            return false;
          }
          if (filter.optionType && cf.optionType !== filter.optionType) {
            return false;
          }
          if (filter.isMultiSelect && !cf.isMultiSelect) {
            return false;
          }
          return true;
        });
        if (!someFiltersMatch) return false;
      } else if (fieldFilter) {
        if (fieldFilter.type && cf.type !== fieldFilter.type) {
          return false;
        }
        if (fieldFilter.subType && cf.subType !== fieldFilter.subType) {
          return false;
        }
        if (
          fieldFilter.optionType &&
          cf.optionType !== fieldFilter.optionType
        ) {
          return false;
        }
        if (fieldFilter.isMultiSelect && !cf.isMultiSelect) {
          return false;
        }
      }

      return true;
    });

    const compatibleField = compatibleCustomFields.find(
      (cf) => cf.id === e2.value
    );
    if (compatibleField) {
      nextFieldMappings[e1.value] = {
        customFieldId: e2.value,
        optionMappings: {},
      };
    }
  }

  for (const jiraField of jiraFields) {
    if (jiraField.field.name === 'Impact') debugger;
    const fieldMapping = nextFieldMappings[jiraField.field.fieldId];
    if (!fieldMapping || !jiraField.field.allowedValues?.length) continue;

    if (!isEmpty(nextFieldMappings[jiraField.field.fieldId]?.optionMappings))
      continue;

    const customField = dsCustomFields.find(
      (cf) => cf.id === fieldMapping.customFieldId
    );
    if (!customField || !customField.options?.length) continue;

    const aiOptionMappings = await inferAnything({
      entity1Name: 'Jira Field Options',
      entity1: jiraField.field.allowedValues.map((option) => ({
        label: option.value || option.name || '',
        value: option.id,
      })),

      entity2Name: 'DevStride Custom Field Options',
      entity2: customField.options.map((option) => ({
        label: option.label,
        value: option.id,
      })),
    });

    const optionMappings: JiraOptionMappings = {};
    for (const { e1, e2 } of aiOptionMappings) {
      optionMappings[e1.value] = e2.value;
    }

    nextFieldMappings[jiraField.field.fieldId].optionMappings = optionMappings;
  }

  return nextFieldMappings;
};

export const inferIssueTypeEntityMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    issueType: JiraIssueType;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraWorkItemTypeMappingValue> => {
  const DEFAULT_VALUE: Readonly<JiraWorkItemTypeMappingValue> = {
    workTypeId: '',
    laneMappings: {},
    fieldMappings: {},
    tagMappings: {},
    priorityMappings: {},
  };
  const next: JiraWorkItemTypeMappingValue =
    cloneDeep<JiraWorkItemTypeMappingValue>(
      opts.subject.projectMappings.workTypeMappings[
        opts.subject.issueType.id
      ] || DEFAULT_VALUE
    );

  const args = (): typeof opts => {
    const updatedProjectMappings: JiraProjectMappingValue = {
      ...opts.subject.projectMappings,
      workTypeMappings: {
        ...opts.subject.projectMappings.workTypeMappings,
        [opts.subject.issueType.id]: next,
      },
    };
    const ret: typeof opts = {
      installationId: opts.installationId,
      subject: {
        project: opts.subject.project,
        issueType: opts.subject.issueType,
        projectMappings: updatedProjectMappings,
      },
    };
    return ret;
  };
  next.workTypeId = await inferIssueTypeWorkTypeAI(args());
  next.laneMappings = await inferIssueTypeLaneMappingsAI(args());
  next.tagMappings = await inferIssueTypeTagMappingsAI(args());
  next.priorityMappings = await inferIssueTypePriorityMappingsAI(args());
  next.fieldMappings = await inferIssueTypeFieldMappingsAI(args());

  return next;
};

export const inferProjectMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    project: JiraProject;
    projectMappings: JiraProjectMappingValue;
  };
}): Promise<JiraProjectMappingValue> => {
  const integrationsStore = useIntegrationsStore();

  const entities = integrationsStore.getJiraInstanceEntities(
    opts.installationId
  );
  if (!entities)
    throw new Error('Bug in code - entities not found, did you load them?');

  const next = cloneDeep(opts.subject.projectMappings);

  if (!next.isEnabled) throw new Error('Bug in code - project is disabled');

  next.laneCollectionId = await inferProjectLaneCollection({
    installationId: opts.installationId,
    subject: opts.subject,
  });

  next.parentNumber = await inferProjectWorkstream({
    installationId: opts.installationId,
    subject: opts.subject,
  });
  if (!next.parentNumber) return next; // don't infer anything else if no workstream is set

  const { jiraIssueTypes } = useJiraProjectEntities({
    installationId: opts.installationId,
    projectId: opts.subject.project.id,
  });

  for (const issueType of jiraIssueTypes.value) {
    next.workTypeMappings[issueType.id] = await inferIssueTypeEntityMappingsAI({
      installationId: opts.installationId,
      subject: {
        project: opts.subject.project,
        issueType,
        projectMappings: next,
      },
    });
  }

  return next;
};

export const inferInstallationUserMappingsAI = async (opts: {
  installationId: IntegrationInstallation['id'];
  subject: {
    userMappings: JiraUserMappings;
  };
}): Promise<JiraUserMappings> => {
  const { orgMembershipCache } = organizationData();

  const entities = useIntegrationsStore().getJiraInstanceEntities(
    opts.installationId
  );

  const next = cloneDeep(opts.subject.userMappings);

  const aiMappings = await inferAnything({
    entity1Name: 'Jira Users',
    entity1:
      entities?.jiraUsers.map((user) => ({
        label: user.displayName || user.accountId,
        value: user.accountId,
      })) || [],

    entity2Name: 'DevStride Users',
    entity2: orgMembershipCache.value.map((member) => ({
      label: member.user.name,
      value: member.userId,
    })),
  });

  for (const { e1, e2 } of aiMappings) {
    if (!next[e1.value]) {
      next[e1.value] = e2.value;
    }
  }

  return next;
};
